import { BonusAmountCard } from "@/entities/bonus"
import { IUser } from "@/shared/api/users"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Pagination } from "@/shared/ui/pagination"
import { Table } from "@/shared/ui/table"
import { ColumnDef } from "@tanstack/react-table"
import { FC } from "react"
import UserSvg from '@/shared/assets/images/User.svg'
import { Link } from "react-router-dom"
import { format, parseISO } from "date-fns"
import { ActionButton } from "@/shared/ui/action-button"
import { useUserAccrualModal } from "@/features/user-accrual"
import { useUserBroadcastModal } from "@/features/user-broadcast"
import AccrualSvg from '@/shared/assets/images/Coins.svg'
import BroadcastSvg from '@/shared/assets/images/Broadcast.svg'
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters"
import CheckSvg from '@/shared/assets/images/Check.svg'
import { useChangeUserGroupModal } from "@/features/change-user-group"
import { useUpdateGroupModal } from "@/features/update-group"
import GroupsSvg from '@/shared/assets/images/UserList.svg'
import VisibilityAclSvg from '@/shared/assets/images/FileLock.svg'
import ProhibitSvg from '@/shared/assets/images/Prohibit.svg'
import { TargetType } from "@/shared/api/groups"
import { useUserBlockModal } from "@/features/user-block"
import { useUserDeleteRestoreModal } from "@/features/user-delete-restore"
import { getDisplayName } from "@/entities/user"
import TrashSvg from '@/shared/assets/images/Trash.svg'
import ArrowsLeftRightSvg from '@/shared/assets/images/ArrowsLeftRight.svg'
import PencilSvg from '@/shared/assets/images/Pencil.svg'
import { useFillProfileModal } from "@/features/fill-profile"
import { selectCommunityInfo } from "@/entities/community"
import { useAppSelector } from "@/shared/lib/redux"

interface UsersTableProps {
    page: number
    totalPages: number
    users: IUser[]
    setPage: (arg0: number) => void
    isLoading: boolean
    onAccrual?: () => void
    labelNoInfo: string
    totalCount: number
    isReportMode?: boolean
    selectedUserIds?: string[]
    onUserSelect?: (user: IUser) => void
    onUserDeselect?: (userId: string) => void
}

const genders = {
    'male': 'Мужской',
    'female': 'Женский'
}

export const UsersTable: FC<UsersTableProps> = ({ page, totalPages, users, isLoading, setPage, onAccrual, labelNoInfo, totalCount, isReportMode = false, selectedUserIds = [], onUserSelect, onUserDeselect }) => {
    const { openUserAccrualModal } = useUserAccrualModal()
    const { openUserBroadcastModal } = useUserBroadcastModal()
    const { openChangeUserGroupModal } = useChangeUserGroupModal()
    const { openVisibilityAclModal } = useUpdateGroupModal()
    const { openConfirmBlockModal } = useUserBlockModal()
    const { openUserDeleteRestoreModal } = useUserDeleteRestoreModal()
    const { openFillProfileModal } = useFillProfileModal()
    const communityInfo = useAppSelector(selectCommunityInfo)

    const columns: ColumnDef<IUser, any>[] = [
        ...(isReportMode ? [{
            accessorFn: (user: IUser) => user,
            enableSorting: false,
            id: 'select',
            cell: (info: any) => {
                const user = info.getValue()
                const isSelected = selectedUserIds.includes(user.id)
                return (
                    <div className="relative flex h-full items-center">
                        <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    onUserSelect?.(user)
                                } else if (!e.target.checked) {
                                    onUserDeselect?.(user.id)
                                }
                            }}
                            className="w-6 h-6 cursor-pointer rounded-full border border-color-dark bg-white transition checked:bg-color-dark appearance-none"
                        />
                        {isSelected && (
                            <div className='absolute w-6 h-6 pointer-events-none text-white items-center justify-center flex'>
                                <CheckSvg width={16} height={16} />
                            </div>
                        )}
                    </div>
                )
            },
            header: () => <div className="!text-start"></div>,
        }] : []),
        {
            accessorFn: (user) => (
                <div className="w-fit flex items-center gap-2">
                    <div className="flex items-center w-9 h-9 bg-color-dark justify-center text-white rounded-full">
                        <UserSvg width={20} height={20} />
                    </div>
                    <div className="flex flex-col gap-[2px]">
                        <Link className="hover:underline" to={`/users/profile/${user.id}`}>
                            {getDisplayName(user)}
                        </Link>
                        <div className="flex flex-col gap-0.5">
                            {user.is_deleted && (
                                <span className="text-xs text-red-600 font-medium">Удалён</span>
                            )}
                            {user.is_deleted && user.deleted_at && (
                                <span className="text-xs text-gray-500">
                                    {format(parseISO(user.deleted_at), 'dd.MM.yyyy HH:mm')}
                                </span>
                            )}
                            {user.is_blocked && !user.is_deleted && (
                                <span className="text-xs text-red-600 font-medium">Заблокирован</span>
                            )}
                        </div>
                    </div>
                </div>
            ),
            enableSorting: false,
            id: 'username',
            cell: info => info.getValue(),
            header: () => <div className="!text-start">Username</div>,
        },
        {
            accessorFn: (user) => (
                <div className="w-fit">
                    {user.name}
                </div>
            ),
            enableSorting: false,
            id: 'name',
            cell: info => info.getValue(),
            header: () => <div className="!text-start">ФИО</div>,
        },
        {
            accessorFn: (user) => (
                <div>
                    {genders[user.gender]}
                </div>
            ),
            enableSorting: false,
            id: 'gender',
            cell: info => info.getValue(),
            header: () => <div className="!text-start">Пол</div>,
        },
        {
            accessorFn: (user) => (
                <div>
                    {user.phone}
                </div>
            ),
            enableSorting: false,
            id: 'phone',
            cell: info => info.getValue(),
            header: () => <div className="!text-start">Телефон</div>,
        },
        {
            accessorFn: (user) => (
                <div>
                    {user.email}
                </div>
            ),
            enableSorting: false,
            id: 'email',
            cell: info => info.getValue(),
            header: () => <div className="!text-start">Почта</div>,
        },
        {
            accessorFn: (user) => (
                <div>
                    {user.date_of_birth &&
                        format(user.date_of_birth, 'dd.MM.yyyy')
                    }
                </div>
            ),
            enableSorting: false,
            id: 'date_of_birth',
            cell: info => info.getValue(),
            header: () => <div className="!text-start">Дата рождения</div>,
        },
        {
            accessorFn: (user) => (
                <div>
                    {user.group?.name}
                </div>
            ),
            enableSorting: false,
            id: 'group',
            cell: info => info.getValue(),
            header: () => <div className="!text-start">Группа</div>,
        },
        {
            accessorFn: (user) => (
                <div className="w-fit">
                    <BonusAmountCard amountNano={user.balance} />
                </div>
            ),
            enableSorting: false,
            id: 'balance',
            cell: info => info.getValue(),
            header: () => <div className="!text-start">Баланс</div>,
        },
        {
            accessorFn: (user) => (
                <div className="w-fit">
                    {toLocaleDateTimeString(user.date_of_registration)}
                </div>
            ),
            enableSorting: false,
            id: 'date_of_registration',
            cell: info => info.getValue(),
            header: () => <div className="!text-start">Дата регистрации</div>,
        },
        {
            accessorFn: (user) => (
                <div className="w-full flex justify-end gap-2">
                    {user.is_deleted ? (
                        <ActionButton
                            onClick={() => openUserDeleteRestoreModal(user, 'recover', onAccrual)}
                            icon={ArrowsLeftRightSvg}
                            title="Восстановить"
                        />
                    ) : (
                        <>
                            {user.is_blocked ? (
                                <ActionButton
                                    onClick={() => openConfirmBlockModal(user, 'unblock', onAccrual)}
                                    icon={CheckSvg}
                                    title="Разблокировать"
                                />
                            ) : (
                                <ActionButton
                                    onClick={() => openConfirmBlockModal(user, 'block', onAccrual)}
                                    icon={ProhibitSvg}
                                    title="Заблокировать"
                                />
                            )}
                            <ActionButton
                                onClick={() => openUserDeleteRestoreModal(user, 'delete', onAccrual)}
                                icon={TrashSvg}
                                title="Удалить"
                            />
                            <ActionButton
                                onClick={() => openUserAccrualModal(user, onAccrual)}
                                icon={AccrualSvg}
                            />
                            <ActionButton
                                onClick={() => openUserBroadcastModal(user, onAccrual)}
                                icon={BroadcastSvg}
                            />
                            <ActionButton
                                onClick={() => openChangeUserGroupModal(user, onAccrual)}
                                icon={GroupsSvg}
                            />
                            <ActionButton
                                onClick={() => openVisibilityAclModal({ id: user.id }, TargetType.USER, onAccrual)}
                                icon={VisibilityAclSvg}
                            />
                            {communityInfo.is_admin_profile_fill_enabled && (
                                <ActionButton
                                    onClick={() => openFillProfileModal(user, onAccrual)}
                                    icon={PencilSvg}
                                    title="Заполнить профиль"
                                />
                            )}
                        </>
                    )}
                </div>
            ),
            enableSorting: false,
            id: 'actions',
            cell: info => info.getValue(),
            header: () => <div />,
        },
    ]

    return (
        <>
            {Boolean(users.length) &&
                <div className="table_box">
                    <Table columns={columns} data={users} />
                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        totalCount={totalCount}
                        setPage={(page) => setPage(page)}
                    />
                </div>
            }
            {(!Boolean(users.length) && isLoading) &&
                <div className="info_panel">
                    <Loader />
                </div>
            }
            {(!Boolean(users.length) && !isLoading) &&
                <NoTableData label={labelNoInfo} />
            }
        </>
    )
}
